import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Select } from '@components/shared/ui/Select';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  message,
  Space,
  Spin,
  Row,
  Col,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import ReturnOrderItemsFormImpl from './ReturnOrderItemsForm';

const { TextArea } = Input;

type ReturnOrderEditRole = 'admin' | 'operator';

// Service / Product hooks are still .js — accept their shape loosely.
type ReturnOrderServiceHook = (options?: { merchantId?: string | null }) => any;
type ProductServiceHook = () => any;

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  [key: string]: unknown;
}

interface ReturnOrderLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface ReturnOrderEditProps {
  /** 退貨單服務 Hook */
  useReturnOrderService: ReturnOrderServiceHook;
  /** 產品服務 Hook */
  useProductService?: ProductServiceHook;
  /** 角色 (例如：'admin' 或 'operator') */
  role?: ReturnOrderEditRole;
  /** Merchant ID — operator 新建模式為當下選的 merchant；編輯模式為 record's merchant */
  merchantId?: string | null;
  /** 由 wrapper 注入的 merchant-scoped 庫存類型；shared 不再自己從 context 讀 */
  inventoryTypes?: InventoryTypeOption[];
  /** 由 wrapper 注入的自定屬性定義 */
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  /** Wrapper 在拿到 record 後呼叫此 callback，讓 wrapper 把 OperatorDataContext.currentMerchant 對齊 record */
  onReturnOrderLoaded?: (returnOrder: ReturnOrderLoadedPayload) => void;
  /** True 期間 spinner 擋住整個表單，避免 merchant-scoped 下拉先用舊 merchant 渲染 */
  merchantDataLoading?: boolean;
}

// Form item shape — fields are produced both by form defaults and by API transforms;
// product_id / inventory_type_id can be either a raw id string, an antd Select option
// object, or null depending on flow.
interface ReturnOrderFormItem {
  key?: string | number;
  id?: string;
  product_id?: { value: string; label: string } | string | null;
  product_sku?: string;
  product_name?: string;
  product_validity_days?: number;
  inventory_type_id?: { value: string; label: string } | string | null;
  expiration_date?: dayjs.Dayjs | string | null;
  manufacturing_date?: dayjs.Dayjs | string | null;
  batch?: string;
  quantity?: number;
  received_quantity?: number;
  note?: string;
  custom_attributes?: Record<string, any>;
  _destroy?: boolean | string;
}

interface ReturnOrderFormValues {
  name?: string;
  order_id?: { value: string; label: string } | string | null;
  note?: string;
  return_order_items_attributes?: ReturnOrderFormItem[];
}

const ReturnOrderEdit: React.FC<ReturnOrderEditProps> = ({
  useReturnOrderService,
  useProductService,
  role = 'admin',
  merchantId = null,
  inventoryTypes: inventoryTypesProp = [],
  inventoryAttributeDefinitions = [],
  onReturnOrderLoaded,
  merchantDataLoading = false,
}) => {
  // ReturnOrderItemsForm 仍是 .jsx，且其 JSDoc 列的 prop 名稱與實際 destructure 不一致
  // （JSDoc 寫 fetchProducts，destructure 是 productService；且未列 inventoryAttributeDefinitions / role）。
  // 用 ComponentType<any> 包一層保留原本 runtime 行為；放在 component body 內避免 Vite HMR TDZ。
  const ReturnOrderItemsForm = ReturnOrderItemsFormImpl as React.ComponentType<any>;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const [form] = Form.useForm<ReturnOrderFormValues>();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // 新建模式預設值：初始一筆空的品項，抽成 callback 供 resetFields 後重新套用
  const applyNewModeDefaults = useCallback(() => {
    if (!isNew) return;
    form.setFieldsValue({
      return_order_items_attributes: [{
        key: Date.now(),
        product_id: null,
        product_sku: '',
        product_name: '',
        product_validity_days: 0,
        inventory_type_id: null,
        expiration_date: null,
        manufacturing_date: null,
        batch: '',
        quantity: 1,
        note: '',
        custom_attributes: {}
      }]
    });
  }, [isNew, form]);

  // shared 元件保持 merchant-agnostic：所有 merchant-scoped 資料（inventoryTypes、
  // inventoryAttributeDefinitions）由 wrapper 對齊到正確的 merchant 後注入。
  // 編輯模式由 wrapper 透過 onReturnOrderLoaded 拿到 record 並把 OperatorDataContext.currentMerchant
  // 對齊到 record 的 merchant；shared 不再直接讀 / 寫 merchant context。

  const addItemRef = useRef<((value: ReturnOrderFormItem) => void) | null>(null);

  const handleAddItem = useCallback(() => {
    if (!addItemRef.current) return;
    addItemRef.current({
      key: Date.now(),
      product_id: null,
      product_sku: '',
      product_name: '',
      product_validity_days: 0,
      inventory_type_id: null,
      expiration_date: null,
      manufacturing_date: null,
      batch: '',
      quantity: 1,
      note: '',
      custom_attributes: {}
    });
    setTimeout(() => {
      const cards = document.querySelectorAll('.product-item-card');
      cards[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  // Call hook with merchantId parameter - useReturnOrderService handles role internally
  const service = useReturnOrderService(role === 'operator' ? { merchantId } : undefined);
  const baseProductService = useProductService ? useProductService() : null;

  // Wrap productService to inject merchant_id if needed (for operator scoping)
  const productService = useMemo(() => {
    if (!baseProductService) return null;
    if (role === 'operator' && merchantId) {
      return {
        ...baseProductService,
        fetchList: (params: Record<string, unknown>) => baseProductService.fetchList({ ...params, merchant_id: merchantId })
      };
    }
    return baseProductService;
  }, [baseProductService, role, merchantId]);

  // Inventory types 由 wrapper 注入（已對齊到正確的 merchant）— shared 不再從 context 讀。
  const inventoryTypes = useMemo(() => {
    return (inventoryTypesProp ?? [])
      .filter((type): type is InventoryTypeOption => Boolean(type?.id))
      .map((type) => ({
        label: type.name ?? type.id,
        value: type.id,
        name: type.name ?? type.id,
      }));
  }, [inventoryTypesProp]);

  // Create a fetch function for inventory types that returns from context
  const fetchInventoryTypes = useCallback(() => {
    return Promise.resolve({
      options: inventoryTypes,
      hasMore: false,
      sinceId: null
    });
  }, [inventoryTypes]);

  // Helper to transform order item to return item form format
  const transformOrderItemToReturnItem = useCallback((item: any): ReturnOrderFormItem => {
    console.log('[ReturnOrderEdit] Transforming Order Item:', item);

    // Extract IDs from nested objects if top-level keys are missing
    const productId = item.product_id || item.product?.id;
    const inventoryTypeId = item.inventory_type_id || item.inventory_type?.id;

    const result: ReturnOrderFormItem = {
      key: Date.now() + Math.random(), // Ensure unique keys
      product_id: productId
        ? {
          value: productId,
          label: item.product_name || item.product?.name || item.product_sku
        }
        : null,
      product_sku: item.product_sku || '',
      product_name: item.product_name || item.product?.name || '',
      product_validity_days: item.product?.validity_days || 0,
      inventory_type_id: item.inventory_type
        ? {
          value: item.inventory_type.id,
          label: item.inventory_type.name
        }
        : inventoryTypeId,
      quantity: item.quantity,
      expiration_date: item.expiration_date ? dayjs(item.expiration_date) : null,
      manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date) : null,
      batch: item.batch,
      note: item.note,
      custom_attributes: item.custom_attributes
    };
    console.log('[ReturnOrderEdit] Transformed Item:', result);
    return result;
  }, []);

  const handleOrderChange = async (selectedOption: { value: string; label: string } | null) => {
    // selectedOption is the full option object from Select (or null if cleared)
    const orderId = selectedOption?.value;

    // Update the form field for order_id - store the full object for Select display
    form.setFieldsValue({ order_id: selectedOption || null });

    if (!orderId) {
      // If order is cleared, reset items to a single empty item
      form.setFieldsValue({
        return_order_items_attributes: [{
          key: Date.now(),
          product_id: null,
          product_sku: '',
          product_name: '',
          product_validity_days: 0,
          inventory_type_id: null,
          expiration_date: null,
          manufacturing_date: null,
          batch: '',
          quantity: 1,
          note: '',
          custom_attributes: {}
        }]
      });
      return;
    }

    if (service.getOrder) {
      try {
        setLoading(true);
        const response = await service.getOrder(orderId);

        if (response.success && response.data) {
          const order = response.data;
          console.log('[ReturnOrderEdit] Fetched Order Data:', order);

          // Map order items to return order items format using helper
          // API response returns 'items', not 'order_items'
          const newItems = (order.order_items || order.items || []).map(transformOrderItemToReturnItem);
          console.log('[ReturnOrderEdit] Final New Items for Form:', newItems);

          if (newItems.length > 0) {
            form.setFieldsValue({
              return_order_items_attributes: newItems
            });
            message.success(t('shared.return_orders.form.items_loaded_from_order') as string);
          } else {
            // Even if order has no items (rare), clear the list or set default
            form.setFieldsValue({ return_order_items_attributes: [] });
          }
        }
      } catch (error) {
        console.error('Failed to load order details:', error);
        message.error(t('shared.return_orders.form.load_order_failed') as string);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await service.suggestName();
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    // 新建模式：自動加入一個空的 item
    applyNewModeDefaults();
  }, [id, applyNewModeDefaults]);

  const loadData = async () => {
    try {
      setDataLoading(true);

      // Load return order data if editing
      if (id) {
        const response = await service.getById(id);
        if (response.success) {
          const returnOrder = response.data;

          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
          // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onReturnOrderLoaded?.(returnOrder);

          // Prepare items data with key and validity_days
          const items: ReturnOrderFormItem[] = (returnOrder.items || []).map((item: any) => {
            // Construct product object
            const productId = item.product_id || item.product?.id;
            const productLabel = item.product?.name || item.product_name || item.product_sku || item.product?.sku;

            // Construct inventory type object
            const inventoryTypeId = item.inventory_type_id || item.inventory_type?.id;
            let inventoryTypeLabel = item.inventory_type?.name || item.inventory_type_name;

            if (!inventoryTypeLabel && inventoryTypeId && inventoryTypes.length > 0) {
              const type = inventoryTypes.find((t) => t.value === inventoryTypeId);
              if (type) inventoryTypeLabel = type.name;
            }

            return {
              ...item,
              key: item.id || Date.now() + Math.random(),
              product_id: productId
                ? {
                  value: productId,
                  label: productLabel
                }
                : null,
              inventory_type_id: inventoryTypeId
                ? {
                  value: inventoryTypeId,
                  label: inventoryTypeLabel
                }
                : null,
              product_validity_days: item.product_validity_days || item.product?.validity_days || 0,
              expiration_date: item.expiration_date ? dayjs(item.expiration_date) : null,
              manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date) : null,
              batch: item.batch || '',
              custom_attributes: item.custom_attributes
            };
          });

          form.setFieldsValue({
            name: returnOrder.name,
            // Set order_id as { value, label } object for Select component to display correctly
            order_id: returnOrder.order
              ? { value: returnOrder.order.id, label: returnOrder.order.name }
              : null,
            note: returnOrder.note,
            return_order_items_attributes: items,
          });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') as string });
          navigate('/return_orders');
        }
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // 組合送出 payload，給兩個提交按鈕共用
  const buildPayload = (values: ReturnOrderFormValues) => {
    // Transform items for API submission
    const items = (values.return_order_items_attributes || []).map((item) => {
      const productIdValue = typeof item.product_id === 'object' && item.product_id !== null
        ? item.product_id.value
        : item.product_id;
      const inventoryTypeIdValue = typeof item.inventory_type_id === 'object' && item.inventory_type_id !== null
        ? item.inventory_type_id.value
        : item.inventory_type_id;

      const submittedItem: Record<string, unknown> = {
        product_id: productIdValue,
        inventory_type_id: inventoryTypeIdValue,
        quantity: item.quantity || 0,
        received_quantity: item.received_quantity || 0,
        note: item.note || '',
        custom_attributes: item.custom_attributes,
      };

      // Only include expiration_date and batch if product has validity_days
      if (item.product_validity_days && item.product_validity_days > 0) {
        if (item.expiration_date) {
          submittedItem.expiration_date = item.expiration_date;
        }
        if (item.batch) {
          submittedItem.batch = item.batch;
        }
      }

      // RO 是需求單據，mfg 永遠可選；只要 user 填了就送出，product flag 不影響可見性。
      if (item.manufacturing_date) {
        submittedItem.manufacturing_date = item.manufacturing_date;
      }

      // Include id and _destroy for existing items (edit mode)
      if (id && item.id) {
        submittedItem.id = item.id;
      }
      if (item._destroy) {
        submittedItem._destroy = item._destroy;
      }

      return submittedItem;
    });

    const orderIdValue = typeof values.order_id === 'object' && values.order_id !== null
      ? values.order_id.value
      : values.order_id;

    const payload: Record<string, unknown> = {
      name: values.name,
      // Handle order_id whether it's an object { value, label } or just a string/null
      order_id: orderIdValue ?? null,
      note: values.note,
      return_order_items_attributes: items,
    };

    if (isNew && merchantId) {
      payload.merchant_id = merchantId;
    }

    return payload;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: ReturnOrderFormValues) => {
    const payload = buildPayload(values);
    return isNew ? await service.create(payload) : await service.update(id, payload);
  };

  const handleSubmit = async (values: ReturnOrderFormValues) => {
    // Validate at least one item
    if (!values.return_order_items_attributes || values.return_order_items_attributes.length === 0) {
      message.error(t('shared.return_orders.form.at_least_one_item') as string);
      return;
    }

    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t('shared.return_orders.form.success') as string);
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = response.data?.id || id;
        navigate(`/return_orders/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t('shared.return_orders.form.failed') as string
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      // 額外檢查至少一筆品項
      if (!values.return_order_items_attributes || values.return_order_items_attributes.length === 0) {
        message.error(t('shared.return_orders.form.at_least_one_item') as string);
        return;
      }

      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('shared.return_orders.form.success') as string);
          // 重置表單，然後重新套用新建模式預設值（一筆空 item）
          form.resetFields();
          applyNewModeDefaults();
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('shared.return_orders.form.failed') as string
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // Render-gate: sync 期間 wrapper 還在把 OperatorDataContext 切到 record 的 merchant，
  // 此時 inventoryTypes / inventoryAttributeDefinitions 還是上一個 merchant 的清單。
  // 必須 early-return 擋住整個 form，<Spin spinning> overlay 不夠（children 仍會 mount
  // 並對著錯 merchant 觸發 fetch）。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('models.return_order.name')}
              required
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="name"
                  noStyle
                  rules={[{ required: true, message: t('common.validation.required') as string }]}
                >
                  <Input placeholder={t('models.return_order.name') as string} />
                </Form.Item>
                {isNew && (
                  <Button
                    onClick={handleSuggestName}
                    loading={suggesting}
                    disabled={loading}
                  >
                    {t('shared.return_orders.form.suggest_name')}
                  </Button>
                )}
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={t('models.return_order.order')}
              name="order_id"
            >
              <Select
                placeholder={t('common.placeholder.select') as string}
                fetchData={service.getOrders}
                pageSize={20}
                isSearchable
                isClearable
                onChange={handleOrderChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('models.return_order.note')}
          name="note"
        >
          <TextArea
            rows={2}
            placeholder={t('models.return_order.note') as string}
          />
        </Form.Item>

        <Form.List name="return_order_items_attributes">
          {(fields, { add, remove }) => {
            addItemRef.current = add;
            return (
            <ReturnOrderItemsForm
              fields={fields}
              add={add}
              remove={remove}
              form={form}
              productService={productService}
              fetchInventoryTypes={fetchInventoryTypes}
              inventoryAttributeDefinitions={inventoryAttributeDefinitions}
              role={role}
            />
            );
          }}
        </Form.List>

        <FloatingActionBarSpacer />
      </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        submitText={t(isNew ? 'common.button.create' : 'common.button.save') as string}
        extraButtons={
          <>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddItem}
            >
              {t('shared.return_orders.form.add_item')}
            </Button>
            {isNew && (
              <Button
                loading={savingAndAdding}
                disabled={loading}
                onClick={handleSaveAndAddAnother}
              >
                {t('common.button.save_and_add_another')}
              </Button>
            )}
          </>
        }
      />
    </>
  );
};

export default ReturnOrderEdit;
